import { supabase } from '../lib/supabase';
import { getLocalUser } from '../utils/authHelpers';

export interface ReadinessItem {
  key: string;
  label: string;
  group: 'setup' | 'agents' | 'connections';
  ready: boolean;
  missingItems: string[];
  settingsHash: string;
}

export interface ReadinessResult {
  ready: boolean;
  loading: boolean;
  isFirstTime: boolean;
  setupItems: ReadinessItem[];
  agentItems: ReadinessItem[];
  connectionItems: ReadinessItem[];
  completedCount: number;
  totalCount: number;
}

export interface ConnectionCheckResult {
  ready: boolean;
  missingConnections: ReadinessItem[];
}

const CHANNEL_CONNECTION_REQUIREMENTS: Record<string, string[]> = {
  Call: ['phone', 'calendar', 'video'],
  Email: ['email', 'calendar', 'video'],
  WhatsApp: ['whatsapp', 'calendar', 'video'],
  LinkedIn: ['linkedin', 'calendar', 'video'],
  'Omni-Channel': ['phone', 'whatsapp', 'email', 'linkedin', 'calendar', 'video'],
  Inbound: ['phone', 'whatsapp', 'email', 'linkedin', 'calendar', 'video'],
};


async function checkProfile(clientId: string, userId: string): Promise<ReadinessItem> {
  const missing: string[] = [];
  try {
    const { data } = await supabase
      .from('user_settings')
      .select('full_name, company_name, email_address, country, company_industry')
      .eq('client_id', clientId)
      .eq('user_id', userId)
      .maybeSingle();

    if (!data) {
      return { key: 'profile', label: 'Profile Info', group: 'setup', ready: false, missingItems: ['Profile not configured'], settingsHash: 'profile' };
    }
    if (!data.full_name) missing.push('Full Name');
    if (!data.company_name) missing.push('Company Name');
    if (!data.email_address) missing.push('Email Address');
    if (!data.country) missing.push('Country');
    if (!data.company_industry) missing.push('Industry');
  } catch {
    missing.push('Could not verify profile');
  }
  return { key: 'profile', label: 'Profile Info', group: 'setup', ready: missing.length === 0, missingItems: missing, settingsHash: 'profile' };
}

async function checkAboutCompany(clientId: string): Promise<ReadinessItem> {
  const missing: string[] = [];
  try {
    const { data } = await supabase
      .from('sales_materials')
      .select('about_company, pitch_book')
      .eq('client_id', clientId)
      .maybeSingle();

    if (!data) {
      return { key: 'about_company', label: 'About Company', group: 'setup', ready: false, missingItems: ['Sales materials not configured'], settingsHash: 'sales-playbook' };
    }

    const ac = data.about_company as Record<string, unknown> | null;
    if (!ac || !ac.products_services) missing.push('Products/Services');
    if (!ac || !ac.usp_statement) missing.push('USP Statement');

    const pb = data.pitch_book as Record<string, unknown[]> | null;
    if (!pb || !Array.isArray(pb.hooks) || pb.hooks.length < 3) missing.push(`Hooks (need 3)`);
    if (!pb || !Array.isArray(pb.elaborations) || pb.elaborations.length < 3) missing.push(`Elaborations (need 3)`);
    if (!pb || !Array.isArray(pb.engaging_questions) || pb.engaging_questions.length < 3) missing.push(`Engaging Questions (need 3)`);
    if (!pb || !Array.isArray(pb.qualifying_questions) || pb.qualifying_questions.length < 2) missing.push(`Qualifying Questions (need 2)`);
    if (!pb || !Array.isArray(pb.objections) || pb.objections.length < 5) missing.push(`Objections (need 5)`);
  } catch {
    missing.push('Could not verify sales materials');
  }
  return { key: 'about_company', label: 'About Company & Pitch Book', group: 'setup', ready: missing.length === 0, missingItems: missing, settingsHash: 'sales-playbook' };
}

async function checkIcp(clientId: string, userId: string): Promise<ReadinessItem> {
  const missing: string[] = [];
  try {
    const { data } = await supabase
      .from('user_settings')
      .select('icp_config')
      .eq('client_id', clientId)
      .eq('user_id', userId)
      .maybeSingle();

    const icp = data?.icp_config as Record<string, unknown[]> | null;
    if (!icp) {
      missing.push('ICP not configured');
    } else {
      if (!Array.isArray(icp.countries) || icp.countries.length === 0) missing.push('Target Countries');
      if (!Array.isArray(icp.industry_ids) || icp.industry_ids.length === 0) missing.push('Target Industries');
      if (!Array.isArray(icp.company_sizes) || icp.company_sizes.length === 0) missing.push('Company Sizes');
    }
  } catch {
    missing.push('Could not verify ICP');
  }
  return { key: 'icp', label: 'Ideal Customer Profile', group: 'setup', ready: missing.length === 0, missingItems: missing, settingsHash: 'sales-playbook' };
}

async function checkAgentIdentity(
  clientId: string,
  agentType: string,
  label: string,
  requiredFields: string[],
  settingsHash: string,
): Promise<{ identityMissing: string[]; row: Record<string, unknown> | null }> {
  const missing: string[] = [];
  try {
    const { data } = await supabase
      .from('agents_settings')
      .select('agents_identity, reply_prompt_text_outbound, reply_prompt_text_inbound, reply_prompt_audio_outbound, reply_prompt_audio_inbound')
      .eq('client_id', clientId)
      .eq('agent_type', agentType)
      .maybeSingle();

    if (!data) {
      missing.push(`${label} not configured`);
      return { identityMissing: missing, row: null };
    }

    const identity = data.agents_identity as Record<string, unknown> | null;
    if (!identity) {
      missing.push(`${label} identity not set up`);
      return { identityMissing: missing, row: data };
    }

    for (const field of requiredFields) {
      const val = identity[field];
      if (!val || (typeof val === 'string' && val.trim() === '')) {
        const readable = field.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        missing.push(readable);
      }
    }
    return { identityMissing: missing, row: data };
  } catch {
    missing.push(`Could not verify ${label}`);
    return { identityMissing: missing, row: null };
  }
}

async function checkCallAgent(clientId: string, isFirstTime: boolean): Promise<ReadinessItem> {
  const { identityMissing } = await checkAgentIdentity(
    clientId, 'call', 'Call Agent',
    ['agent_name', 'gender', 'tone', 'role', 'phone_number'],
    'call-agent'
  );
  const missing = [...identityMissing];

  if (isFirstTime && missing.length === 0) {
    try {
      const { count } = await supabase
        .from('call_scripts')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .eq('status', 'approved');
      if (!count || count === 0) missing.push('Approved call scripts');
    } catch {
      missing.push('Could not verify call scripts');
    }
  }

  return { key: 'call_agent', label: 'Call Agent', group: 'agents', ready: missing.length === 0, missingItems: missing, settingsHash: 'call-agent' };
}

async function checkWhatsAppAgent(clientId: string, isFirstTime: boolean): Promise<ReadinessItem> {
  const { identityMissing, row } = await checkAgentIdentity(
    clientId, 'whatsapp', 'WhatsApp Agent',
    ['agent_name', 'gender', 'tone', 'role', 'objective'],
    'whatsapp-agent'
  );
  const missing = [...identityMissing];

  if (row) {
    if (!row.reply_prompt_text_outbound) missing.push('Reply prompts not generated');
    else {
      if (!row.reply_prompt_text_inbound) missing.push('Inbound text reply prompt');
      if (!row.reply_prompt_audio_outbound) missing.push('Outbound audio reply prompt');
      if (!row.reply_prompt_audio_inbound) missing.push('Inbound audio reply prompt');
    }
  }

  if (isFirstTime) {
    try {
      const { count } = await supabase
        .from('whatsapp_templates')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .eq('template_key', 'wa_outreach_first')
        .eq('status', 'approved');
      if (!count || count === 0) missing.push('First outreach template must be approved');
    } catch {
      missing.push('Could not verify WhatsApp templates');
    }
  }

  return { key: 'whatsapp_agent', label: 'WhatsApp Agent', group: 'agents', ready: missing.length === 0, missingItems: missing, settingsHash: 'whatsapp-agent' };
}

async function checkEmailAgent(clientId: string, isFirstTime: boolean): Promise<ReadinessItem> {
  const { identityMissing, row } = await checkAgentIdentity(
    clientId, 'email', 'Email Agent',
    ['agent_name', 'role', 'objective', 'tone', 'email_layout', 'signature'],
    'email-agent'
  );
  const missing = [...identityMissing];

  if (row) {
    if (!row.reply_prompt_text_outbound) missing.push('Reply prompts not generated');
    else if (!row.reply_prompt_text_inbound) missing.push('Inbound text reply prompt');
  }

  if (isFirstTime && missing.length === 0) {
    try {
      const { count } = await supabase
        .from('email_templates')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .eq('template_key', 'email_outreach_pitch')
        .eq('status', 'approved');
      if (!count || count === 0) missing.push('First outreach email template must be approved');
    } catch {
      missing.push('Could not verify email templates');
    }
  }

  return { key: 'email_agent', label: 'Email Agent', group: 'agents', ready: missing.length === 0, missingItems: missing, settingsHash: 'email-agent' };
}

async function checkLinkedInAgent(clientId: string, isFirstTime: boolean): Promise<ReadinessItem> {
  const { identityMissing, row } = await checkAgentIdentity(
    clientId, 'linkedin', 'LinkedIn Agent',
    ['agent_persona', 'role', 'objective', 'tone'],
    'linkedin-agent'
  );
  const missing = [...identityMissing];

  if (row) {
    if (!row.reply_prompt_text_outbound) missing.push('Reply prompts not generated');
    else if (!row.reply_prompt_audio_outbound) missing.push('Outbound audio reply prompt');
  }

  if (isFirstTime && missing.length === 0) {
    try {
      const { count } = await supabase
        .from('linkedin_templates')
        .select('id', { count: 'exact', head: true })
        .eq('client_id', clientId)
        .eq('status', 'approved');
      if (!count || count === 0) missing.push('Approved LinkedIn templates');
    } catch {
      missing.push('Could not verify LinkedIn templates');
    }
  }

  return { key: 'linkedin_agent', label: 'LinkedIn Agent', group: 'agents', ready: missing.length === 0, missingItems: missing, settingsHash: 'linkedin-agent' };
}

async function checkConnection(
  clientId: string,
  table: string,
  key: string,
  label: string,
  settingsHash: string,
): Promise<ReadinessItem> {
  try {
    const { count } = await supabase
      .from(table)
      .select('*', { count: 'exact', head: true })
      .eq('client_id', clientId)
      .eq('is_active', true);

    const ready = (count ?? 0) > 0;
    return {
      key,
      label,
      group: 'connections',
      ready,
      missingItems: ready ? [] : [`No active ${label.toLowerCase()} connected`],
      settingsHash,
    };
  } catch {
    return {
      key,
      label,
      group: 'connections',
      ready: false,
      missingItems: [`Could not verify ${label.toLowerCase()}`],
      settingsHash,
    };
  }
}

export async function checkOutreachReadiness(clientId: string): Promise<ReadinessResult> {
  const localUser = getLocalUser();
  const userId = localUser?.email ?? '';

  const { data: settingsRow } = await supabase
    .from('user_settings')
    .select('first_outreach_completed_at')
    .eq('client_id', clientId)
    .eq('user_id', userId)
    .maybeSingle();

  const isFirstTime = !settingsRow?.first_outreach_completed_at;

  const [
    profile,
    aboutCompany,
    icp,
    callAgent,
    whatsappAgent,
    emailAgent,
    linkedinAgent,
    phone,
    whatsapp,
    email,
    linkedin,
    calendar,
    video,
  ] = await Promise.all([
    checkProfile(clientId, userId),
    checkAboutCompany(clientId),
    checkIcp(clientId, userId),
    checkCallAgent(clientId, isFirstTime),
    checkWhatsAppAgent(clientId, isFirstTime),
    checkEmailAgent(clientId, isFirstTime),
    checkLinkedInAgent(clientId, isFirstTime),
    checkConnection(clientId, 'phone_numbers', 'phone', 'Phone Number', 'phone-numbers'),
    checkConnection(clientId, 'whatsapp_accounts', 'whatsapp', 'WhatsApp Account', 'whatsapp-connection'),
    checkConnection(clientId, 'email_connections', 'email', 'Email Connection', 'email-connection'),
    checkConnection(clientId, 'linkedin_accounts', 'linkedin', 'LinkedIn Account', 'linkedin-connection'),
    checkConnection(clientId, 'calendar_connections', 'calendar', 'Calendar', 'calendars'),
    checkConnection(clientId, 'video_connections', 'video', 'Video Calls', 'video-calls'),
  ]);

  const setupItems = [profile, aboutCompany, icp];
  const agentItems = [callAgent, whatsappAgent, emailAgent, linkedinAgent];
  const connectionItems = [phone, whatsapp, email, linkedin, calendar, video];

  const allItems = [...setupItems, ...agentItems, ...connectionItems];
  const completedCount = allItems.filter(i => i.ready).length;
  const totalCount = allItems.length;
  const ready = allItems.every(i => i.ready);

  return {
    ready,
    loading: false,
    isFirstTime,
    setupItems,
    agentItems,
    connectionItems,
    completedCount,
    totalCount,
  };
}

export async function checkConnectionsForChannel(
  clientId: string,
  channelAction: string,
): Promise<ConnectionCheckResult> {
  const requiredKeys = CHANNEL_CONNECTION_REQUIREMENTS[channelAction];
  if (!requiredKeys) return { ready: true, missingConnections: [] };

  const connectionMap: Record<string, { table: string; label: string; settingsHash: string }> = {
    phone: { table: 'phone_numbers', label: 'Phone Number', settingsHash: 'phone-numbers' },
    whatsapp: { table: 'whatsapp_accounts', label: 'WhatsApp Account', settingsHash: 'whatsapp-connection' },
    email: { table: 'email_connections', label: 'Email Connection', settingsHash: 'email-connection' },
    linkedin: { table: 'linkedin_accounts', label: 'LinkedIn Account', settingsHash: 'linkedin-connection' },
    calendar: { table: 'calendar_connections', label: 'Calendar', settingsHash: 'calendars' },
    video: { table: 'video_connections', label: 'Video Calls', settingsHash: 'video-calls' },
  };

  const checks = requiredKeys.map(key => {
    const cfg = connectionMap[key];
    return checkConnection(clientId, cfg.table, key, cfg.label, cfg.settingsHash);
  });

  const results = await Promise.all(checks);
  const missingConnections = results.filter(r => !r.ready);

  return {
    ready: missingConnections.length === 0,
    missingConnections,
  };
}

export interface TestReadinessCategory {
  label: string;
  ready: boolean;
}

export interface TestReadinessResult {
  ready: boolean;
  categories: TestReadinessCategory[];
}

type TestAgentType = 'call' | 'whatsapp' | 'email' | 'linkedin';

const TEST_CONNECTION_MAP: Record<TestAgentType, { table: string; key: string; label: string; settingsHash: string }> = {
  call: { table: 'phone_numbers', key: 'phone', label: 'Phone Number Connection', settingsHash: 'phone-numbers' },
  whatsapp: { table: 'whatsapp_accounts', key: 'whatsapp', label: 'WhatsApp Account Connection', settingsHash: 'whatsapp-connection' },
  email: { table: 'email_connections', key: 'email', label: 'Email Connection', settingsHash: 'email-connection' },
  linkedin: { table: 'linkedin_accounts', key: 'linkedin', label: 'LinkedIn Account Connection', settingsHash: 'linkedin-connection' },
};

const AGENT_CHECKERS: Record<TestAgentType, (clientId: string, isFirstTime: boolean) => Promise<ReadinessItem>> = {
  call: checkCallAgent,
  whatsapp: checkWhatsAppAgent,
  email: checkEmailAgent,
  linkedin: checkLinkedInAgent,
};

export async function checkTestAgentReadiness(
  clientId: string,
  agentType: TestAgentType,
): Promise<TestReadinessResult> {
  const localUser = getLocalUser();
  const userId = localUser?.email ?? '';
  const conn = TEST_CONNECTION_MAP[agentType];

  const [profile, aboutCompany, agentResult, connectionResult] = await Promise.all([
    checkProfile(clientId, userId),
    checkAboutCompany(clientId),
    AGENT_CHECKERS[agentType](clientId, true),
    checkConnection(clientId, conn.table, conn.key, conn.label, conn.settingsHash),
  ]);

  const categories: TestReadinessCategory[] = [
    { label: 'Company Profile & Sales Playbook', ready: profile.ready && aboutCompany.ready },
    { label: 'Agent Settings & Scripts', ready: agentResult.ready },
    { label: conn.label, ready: connectionResult.ready },
  ];

  return {
    ready: categories.every(c => c.ready),
    categories,
  };
}

export async function markFirstOutreachCompleted(clientId: string): Promise<void> {
  try {
    const localUser = getLocalUser();
    const userId = localUser?.email ?? '';

    const { data } = await supabase
      .from('user_settings')
      .select('first_outreach_completed_at')
      .eq('client_id', clientId)
      .eq('user_id', userId)
      .maybeSingle();

    if (data && !data.first_outreach_completed_at) {
      await supabase
        .from('user_settings')
        .update({ first_outreach_completed_at: new Date().toISOString() })
        .eq('client_id', clientId)
        .eq('user_id', userId);
    }
  } catch (err) {
    console.error('[readinessService] Failed to mark first outreach completed:', err);
  }
}
